/**
 * @fileoverview Hooks for fetching Indirect Costs with pagination.
 * @module features/indirect-costs/hooks/useIndirectCosts
 */

import { useInfiniteQuery } from '@tanstack/react-query'
import { addMonths, formatISO, startOfMonth } from 'date-fns'
import { listIndirectCosts } from '../api/indirectCostsService'
import type { PageIndirectCostResponse } from '../schemas/indirectCostsSchema'

/**
 * Create month range [start, end) in YYYY-MM-DD.
 *
 * @param year The calendar year
 * @param monthIndex Zero-based month index (0=Jan)
 */
export function monthRange(year: number, monthIndex: number): { start: string; end: string } {
  const start = startOfMonth(new Date(year, monthIndex, 1))
  const end = addMonths(start, 1)
  // Format as YYYY-MM-DD only
  const fmt = (d: Date): string => formatISO(d, { representation: 'date' })
  return { start: fmt(start), end: fmt(end) }
}

/**
 * useIndirectCosts
 * Infinite query that pages by 50 rows within a selected month.
 *
 * @param year Year number (e.g., 2025)
 * @param monthIndex 0-11 month index
 * @param pageSize Page size, default 50
 */
import type { UseInfiniteQueryResult, InfiniteData } from '@tanstack/react-query'

export function useIndirectCosts(
  year: number,
  monthIndex: number,
  pageSize = 50
): UseInfiniteQueryResult<InfiniteData<PageIndirectCostResponse>, Error> {
  const { start, end } = monthRange(year, monthIndex)
  return useInfiniteQuery<PageIndirectCostResponse, Error>({
    queryKey: ['indirect-costs', year, monthIndex, pageSize, start, end],
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<PageIndirectCostResponse> => {
      const page = typeof pageParam === 'number' ? pageParam : 0
      return listIndirectCosts({
        startDate: start,
        endDate: end,
        page,
        size: pageSize,
        sort: 'updatedAt,desc',
      })
    },
    getNextPageParam: lastPage => {
      // Prefer Spring-style pagination when provided
      if (typeof lastPage.number === 'number' && typeof lastPage.totalPages === 'number') {
        const next = lastPage.number + 1
        return next < lastPage.totalPages ? next : undefined
      }
      if (typeof lastPage.last === 'boolean' && typeof lastPage.number === 'number') {
        const next = lastPage.number + 1
        return lastPage.last ? undefined : next
      }
      // Fallback: infer pagination from page size/content length
      const expectedSize = lastPage.size ?? pageSize
      return lastPage.content.length < expectedSize
        ? undefined
        : typeof lastPage.number === 'number'
          ? lastPage.number + 1
          : 1
    },
  })
}
